import { EntityController } from "./pages/entity/entity-controller";
import { Router, RouterConfiguration } from 'aurelia-router';
import { autoinject, PLATFORM, computedFrom, LogManager } from "aurelia-framework";
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthService } from 'aurelia-authentication';
import { DirectoryService } from "./data/service/directory-service";
import { DirectoryInfo } from './data/entities/directory-info';
import { AppEvents } from './core/app-events';
import { NotificationInfoService } from './data/service/notification-info-service';
import { AppStateService } from './data/service/app-state-service';
import { MetadataService } from 'data/service/meta-data-service';
import { AccountService } from 'data/service/account-service';
import { SearchInput } from './data/entities/input/searchInput';
import { Item } from './data/entities/item';
import { SearchInfoService } from './data/service/search-info-service';

@autoinject
export class App {
	notificationCountSubscription: Subscription;
	statusMessageSubscription: Subscription;
	authenticationChangeSubscription: Subscription;
	directoryAddedSubscription: Subscription;
	changeDirectorySubscription: Subscription;
	notificationTimerId: number;
	showMessage = false;
	directories: Array<DirectoryInfo>;
	router: Router;
	hasMetadata: boolean;
	message = "Welcome to Traken";
	logger = LogManager.getLogger('App');
	currentTeam: string;
	currentUserEmail: string;
	notificationCount = 0;
	myKeypressCallback: any;
	items: Array<Item>;
	item: SearchInput = new SearchInput();

	constructor(private entityController: EntityController,
		private service: SearchInfoService,
		private directoryService: DirectoryService,
		private eventAggregator: EventAggregator,
		private authService: AuthService,
		private notificationService: NotificationInfoService,
		private appStateService: AppStateService,
		private metadataService: MetadataService,
		private accountService: AccountService) {
		this.logger.info("app.constructor" + entityController);
		this.myKeypressCallback = (e: KeyboardEvent) => {
			this.logger.info(e.keyCode.toString());
			if (e.keyCode === 13) {
				this.search();
			}
		};

		window.addEventListener('keypress', this.myKeypressCallback, false);
	}

	//This is used by the navigation bar to filter out roles that the user is not authorized to view
	get roles() {
		return this.accountService.currentUser.appRoles;
	}

	//Clean Up
	unsubscribeToEvents(): any {
		this.notificationCountSubscription.dispose();
		this.authenticationChangeSubscription.dispose();
		this.authenticationChangeSubscription.dispose();
		this.directoryAddedSubscription.dispose();
		this.changeDirectorySubscription.dispose();
	}

	menuClick(event) {
		const args = event.detail;
		if (args.ID) {
			this.logger.info('Menu item id "' + args.ID + '" is clicked.');
			this.changeDirectory(args.ID);
		} else {
			this.logger.info('Menu item is clicked.');
		}
	}

	stopNotificationCountTimer(): any {
		clearInterval(this.notificationTimerId);
		this.notificationCount = 0;
	}

	async getNotificationCount() {
		this.notificationService.getNotificationCount();
		this.getDirectories();//Update the Notification Count shown in the Directories.
	}

	async changeDirectory(appUserId) {
		await this.directoryService.setDefaultDirectory(appUserId);
		this.clearData();
		await this.initializeData();
		this.eventAggregator.publish(AppEvents.DIRECTORY_CHANGED, appUserId);
	}

	// make a getter to get the authentication status.
	// use computedFrom to avoid dirty checking
	@computedFrom('authService.authenticated')
	get authenticated() {
		return this.authService.authenticated;
	}

	async initializeData() {
		this.currentUserEmail = this.accountService.currentUserName;
		await this.metadataService.getMetadata();
		await this.getDirectories();
		this.startNotificationCountTimer();
		this.eventAggregator.publish(AppEvents.REFRESH_EXPLORER);
	}

	clearData() {
		this.logger.info("clearData()");
		this.clearMetadata();
		this.appStateService.clearData();
		this.stopNotificationCountTimer();
	}

	async search() {
		this.items = null;
		this.eventAggregator.publish(AppEvents.ToggleIsBusyVisiblity, true);
		this.items = await this.service.search(this.item);
		this.eventAggregator.publish(AppEvents.ToggleIsBusyVisiblity, false);
		if (this.items.length === 0) {
			this.message = "Sorry, I looked, but found nothing.";
			this.setMessageTimeout();
		} else {
			this.router.navigate(`#/app/explorer/entity/${this.items[0].id}`);
		}
	}

	setMessageTimeout() {
		window.setTimeout(() => this.message = '', 3000);
	}

	async onAuthenticated(authenticated) {
		this.logger.info("app.onAuthenticated()")
		if (authenticated) {
			this.accountService.setAuthResponse(this.authService.getIdToken());
			await this.initializeData();
		}
		else {
			this.clearData();
			//this.accountService.logout();
		}
	}

	async initialize() {
	}

	startNotificationCountTimer() {
		this.getNotificationCount();
		this.notificationTimerId = window.setInterval(() => this.getNotificationCount(), 600000);
	}

	clearMetadata() {
		this.logger.info("app.clearMetadata()");
		this.hasMetadata = false;
		//this.entityController.clearMetadata();
	}

	//The current directory is stored on the server and is gotten any time the directly changes.
	async getDirectories() {
		const items = await this.directoryService.listAsync();
		this.directories = items;
		this.currentTeam = items.find(a => a.isDefault === true).team;
		this.eventAggregator.publish(AppEvents.DIRECTORY_LIST_CHANGED, items);
	}

	created() {
		this.logger.info("app.created");
	}

	deactivate() {
		this.logger.info("app.deactivate()");
		this.unsubscribeToEvents();
		window.removeEventListener('keypress', this.myKeypressCallback);
	}

	subscribeToEvents() {
		this.authenticationChangeSubscription = this.eventAggregator.subscribe(AppEvents.AUTHENTICATION_CHANGE, authenticated => {
			this.onAuthenticated(authenticated);
		});
		this.statusMessageSubscription = this.eventAggregator.subscribe(AppEvents.STATUS_MESSAGE, message => {
			this.message = message;
			this.showMessage = true;
		});

		this.notificationCountSubscription = this.eventAggregator.subscribe(AppEvents.NOTIFICATION_COUNT_CHANGED, message => {
			this.notificationCount = message;
		});
		this.directoryAddedSubscription = this.eventAggregator.subscribe(AppEvents.DIRECTORY_ADDED, message => this.onDirectoryAdded());
		this.changeDirectorySubscription = this.eventAggregator.subscribe(AppEvents.CHANGE_DIRECTORY, message => this.changeDirectory(message));
	}

	onDirectoryAdded() {
		this.getNotificationCount();
	}

	async activate() {
		this.subscribeToEvents();
		this.logger.info("app.activate");
		if (this.authenticated) {
			this.onAuthenticated(true);
		}
		//Metadata should be gotten when authenticated, not during activate as person may not yet be logged in.
		//await this.metadataService.getMetadata();
	}
	
	configureRouter(config: RouterConfiguration, router: Router) {
		config.title = 'Traken';
		config.map([
			{ route: [''], redirect: 'dashboard' },
			{ route: ['dashboard'], name: 'dashboard', moduleId: PLATFORM.moduleName('pages/dashboard/dashboard'), nav: true, title: 'Dashboard' },
			{ route: ['explorer'], name: 'explorer', moduleId: PLATFORM.moduleName('pages/entity/entity-shell'), ref: '/explorer', nav: true, title: 'Explorer', auth: true },
			{ route: ['team'], name: 'team', moduleId: PLATFORM.moduleName('pages/team/team'), nav: true, title: 'Team', auth: true, roles: ['ADMIN', 'OWNER'] },
			{ route: ['create-team'], name: 'createTeam', moduleId: PLATFORM.moduleName('pages/team/create-team'), roles: ['ADMIN', 'OWNER'], nav: false, auth: true },
			{ route: ['invite'], name: 'invite', moduleId: PLATFORM.moduleName('pages/team/invite-view'), nav: true, title: 'Invite', auth: true, roles: ['ADMIN', 'OWNER'] },
			{ route: ['notifications'], name: 'notifications', moduleId: PLATFORM.moduleName('pages/notifications/notifications'), title: 'Inbox', auth: true, settings: { showNotificationCount: true } },
			{ route: ['expired'], name: 'expired', moduleId: PLATFORM.moduleName('account/expired') },

			{ route: ['search'], name: 'search', moduleId: PLATFORM.moduleName('pages/search/search'), nav: true, title: 'Search' },
			{ route: ['settings'], name: 'settings', moduleId: PLATFORM.moduleName('pages/settings/settings'), title: 'Settings' },
			{ route: ['support',], name: 'support', moduleId: PLATFORM.moduleName('pages/support/support-home'), title: 'Support' },
			//{ route: ['support-queue',], name: 'supportQueue', moduleId: PLATFORM.moduleName('pages/support/support-queue'), nav: true, title: 'Support Queue', roles:['SUPPORT'] },
			{ route: ['addTicket',], name: 'addTicket', moduleId: PLATFORM.moduleName('pages/support/add-ticket') },
			{ route: ['ticket/:id',], name: 'viewTicket', moduleId: PLATFORM.moduleName('pages/support/view-ticket') },
			//{ route: ['test',], name: 'test', moduleId: PLATFORM.moduleName('pages/test/chart/test'), nav: true, title: 'test' },
		]);

		this.router = router;
	}
}
